/* ---------- Commune boundary loader (Nominatim -> GeoJSON) ---------- */
const __communeCache = {};

export async function fetchCommunePolygon(name) {
  if (__communeCache[name]) return __communeCache[name];
  const lsKey = 'commune-poly-' + name;

  try {
    const cached = localStorage.getItem(lsKey);
    if (cached) {
      __communeCache[name] = JSON.parse(cached);
      return __communeCache[name];
    }
  } catch (e) {}

  const url =
    'https://nominatim.openstreetmap.org/search?format=json&polygon_geojson=1&limit=1&countrycodes=be&q=' +
    encodeURIComponent(name + ', Belgique');

  try {
    const r = await fetch(url, { headers: { 'Accept-Language': 'fr' } });
    const data = await r.json();
    const geo = data[0]?.geojson;
    if (geo) {
      __communeCache[name] = geo;
      try {
        localStorage.setItem(lsKey, JSON.stringify(geo));
      } catch (e) {}
      return geo;
    }
  } catch (e) {
    console.warn('Polygon fetch failed for', name, e);
  }

  return null;
}

/* ---------- Address geocoder (Nominatim, Belgium only) ----------
   Utilisé pour placer un lieu sur la carte quand l'API ne fournit pas de
   coordonnées. On force la Belgique (countrycodes=be + ", Belgique") pour
   éviter qu'une ville homonyme d'un autre pays ne soit retournée. */
const __addrCache = {};

// Bbox Belgique : minLon,minLat,maxLon,maxLat
const BE_BBOX = '2.55,49.49,6.41,51.51';

async function __geocodeBe(query) {
  if (!query) return null;
  const key = query.toLowerCase();
  if (__addrCache[key]) return __addrCache[key];
  const lsKey = 'geo-be-' + key;

  try {
    const cached = localStorage.getItem(lsKey);
    if (cached) {
      __addrCache[key] = JSON.parse(cached);
      return __addrCache[key];
    }
  } catch (e) {}

  // Photon (komoot) : géocodeur OSM avec CORS activé et sans clé API.
  // On restreint à la Belgique (bbox) + filtre countrycode pour éviter les
  // homonymes d'autres pays. (Nominatim public n'envoie pas d'en-têtes CORS
  // sur ses réponses d'erreur/429 → inutilisable de façon fiable côté navigateur.)
  const url =
    'https://photon.komoot.io/api/?limit=1&lang=fr&bbox=' + BE_BBOX +
    '&q=' + encodeURIComponent(query);

  try {
    const r = await fetch(url);
    const data = await r.json();
    const f = data && data.features && data.features[0];
    if (f && f.geometry && Array.isArray(f.geometry.coordinates)) {
      const cc = f.properties && f.properties.countrycode;
      const lonlat = [f.geometry.coordinates[0], f.geometry.coordinates[1]];
      if ((!cc || cc === 'BE') && Number.isFinite(lonlat[0]) && Number.isFinite(lonlat[1])) {
        __addrCache[key] = lonlat;
        try {
          localStorage.setItem(lsKey, JSON.stringify(lonlat));
        } catch (e) {}
        return lonlat;
      }
    }
  } catch (e) {
    console.warn('Geocoding failed for', query, e);
  }

  return null;
}

export async function fetchAddressLonLat(parts = {}) {
  const clean = (s) => (s == null ? '' : String(s).trim());
  const street = clean(parts.street);
  const postcode = clean(parts.postcode);
  const city = clean(parts.city || parts.commune);

  // 1) Adresse complète, 2) repli code postal + ville, 3) repli ville seule —
  // toujours restreint à la Belgique.
  const attempts = [
    [street, postcode, city].filter(Boolean).join(', '),
    [postcode, city].filter(Boolean).join(', '),
    city,
  ].filter((q, i, arr) => q && arr.indexOf(q) === i);

  for (const q of attempts) {
    const ll = await __geocodeBe(q);
    if (ll) return ll;
  }
  return null;
}

export function addCommuneBoundaryLayer(map, adminCommunes, opts = {}) {
  if (!map || !window.ol || !Array.isArray(adminCommunes)) return;

  const src = new ol.source.Vector();
  const layer = new ol.layer.Vector({
    source: src,
    style: new ol.style.Style({
      fill: new ol.style.Fill({ color: opts.fill || 'rgba(255, 62, 143, 0.10)' }),
      stroke: new ol.style.Stroke({
        color: opts.stroke || '#0F0F0F',
        width: opts.width || 2,
        lineDash: opts.dash || [6, 4]
      })
    })
  });

  // Insert just above base tiles so pins stay on top
  map.getLayers().insertAt(map.getLayers().getLength() - 1, layer);

  adminCommunes.forEach((name, idx) => {
    setTimeout(async () => {
      const geo = await fetchCommunePolygon(name);
      if (!geo) return;

      const reader = new ol.format.GeoJSON();
      const feats = reader.readFeatures(
        { type: 'Feature', geometry: geo },
        { featureProjection: 'EPSG:3857' }
      );
      src.addFeatures(feats);

      if (opts.fit && src.getFeatures().length) {
        map.getView().fit(src.getExtent(), {
          padding: opts.padding || [40, 40, 40, 40],
          maxZoom: opts.maxZoom || 13,
          duration: opts.duration || 400
        });
      }
    }, idx * 1100);
  });

  return layer;
}

/* ---------- Fond de carte sans clé API ----------
   Carto (basemaps.cartocdn.com) exige désormais une clé : ses tuiles
   renvoyaient une image « API KEY REQUIRED ». On utilise les tuiles
   OpenStreetMap standard, libres et sans token. La classe `ol-basemap`
   leur redonne en CSS le rendu pâle attendu par le design (le fond
   Carto était gris clair, OSM est coloré).
   L'attribution OSM est portée par la source : la laisser visible sur au
   moins une carte de la page est une condition d'usage des tuiles. */
export function baseTileLayer(opts = {}) {
  return new ol.layer.Tile({
    className: 'ol-layer ol-basemap',
    source: new ol.source.OSM(),
    opacity: opts.opacity == null ? 1 : opts.opacity
  });
}

/* ---------- Mini OpenLayers map (inline) ---------- */
export const MiniMap = ({ lonlats, center = [4.55, 50.66], zoom = 10, pinColor = '#C5694A', style }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current || !window.ol) return;

    const features = (lonlats || []).map((ll) => new ol.Feature({
      geometry: new ol.geom.Point(ol.proj.fromLonLat(ll))
    }));

    const vs = new ol.source.Vector({ features });
    const map = new ol.Map({
      target: ref.current,
      controls: [],
      interactions: ol.interaction.defaults.defaults({ mouseWheelZoom: false, dragPan: false }),
      layers: [
        baseTileLayer(),
        new ol.layer.Vector({
          source: vs,
          style: new ol.style.Style({
            image: new ol.style.Circle({
              radius: 8,
              fill: new ol.style.Fill({ color: pinColor }),
              stroke: new ol.style.Stroke({ color: '#F7F1E5', width: 2 })
            })
          })
        })
      ],
      view: new ol.View({
        center: ol.proj.fromLonLat(center),
        zoom
      })
    });

    if (features.length > 1) {
      map.getView().fit(vs.getExtent(), { padding: [20, 20, 20, 20], maxZoom: 11 });
    }

    return () => map.setTarget(undefined);
  }, []);

  return <div ref={ref} className="ol-map" style={style} />;
};

/* ---------- Belgium silhouette map ---------- */
export const BelgiumMap = ({ style, pins = [] }) =>
  <svg viewBox="0 0 360 280" style={style} aria-hidden="true">
    <path
      d="M48,76 L72,52 L96,46 L124,38 L154,32 L184,40 L214,36 L240,28 L264,42 L286,58 L308,74 L320,98 L312,124 L298,142 L278,156 L262,176 L246,194 L234,216 L210,224 L182,222 L156,236 L130,244 L106,238 L84,224 L70,202 L62,176 L52,148 L42,122 L40,100 Z"
      fill="var(--paper-2)"
      stroke="var(--ink)"
      strokeWidth="1.5"
      opacity="0.9"
    />
    <path
      d="M158,134 L188,128 L214,134 L226,152 L218,170 L194,178 L168,176 L154,160 Z"
      fill="var(--terracotta)"
      opacity="0.25"
      stroke="var(--terracotta)"
      strokeWidth="1.2"
      strokeDasharray="2 3"
    />
    <text x="186" y="158" fontFamily="JetBrains Mono" fontSize="9" fill="var(--ink)" textAnchor="middle">Brabant wallon</text>
    {pins.map((p, i) => (
      <g key={i}>
        <circle cx={p[0]} cy={p[1] + 6} r="7" fill={p[2]} opacity="0.3" />
        <circle cx={p[0]} cy={p[1]} r="6" fill={p[2]} />
        <circle cx={p[0]} cy={p[1]} r="2" fill="var(--paper)" />
      </g>
    ))}
  </svg>;
